import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {Chart, Filler, LineElement, PointElement, RadarController, RadialLinearScale} from 'chart.js'

import {RadarTooltip, tooltipWidth} from '$components/insurance/chart/radarTooltip'
import {GraphCoverages} from '$hooks/insurance/my/home/diagnosis/useDiagnosisCoverage'
import useResize from '$hooks/useResize'
import {
    DATASET_LABEL,
    drawPoint,
    getAngle,
    getCanvasToParentPosition,
    getLabelPosition,
    getRelativeCanvasPosition,
    getTooltipLeftByIndex,
    getTooltipTopByIndex,
    getWindowCalculatedLeft,
    GRAPH_COLOR,
    labelDrawPlugin,
    labelFontSize,
    labelPaddingX,
    labelPaddingY,
    maxTicksLimit,
} from '$utils/chart'

interface TooltipStatus {
    display: boolean // 툴팁 노출여부 결정
    index: number // 선택된 elements 식별을 위해 사용
    left: number
    top: number
}
// 툴팁 기본 상태값
const styleInitState: TooltipStatus = {
    display: false,
    index: -1,
    left: 0,
    top: 0,
}

// chart options
const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
        tooltip: {
            enabled: false,
        },
        legend: {
            display: false,
        },
    },
    scales: {
        r: {
            max: 10_000_000,
            beginAtZero: true,
            ticks: {
                display: false,
                stepSize: 2_500_000,
                maxTicksLimit,
            },
            pointLabels: {
                display: false,
            },

            grid: {
                z: 2, // 그래프 위에 그리드선 보이도록
            },
        },
    },
    layout: {
        autoPadding: true,
        padding: {
            top: 35,
            right: 35,
            left: 35,
            bottom: 35,
        },
    },
}

const getNormalize = (data: number[], maxValues: number[]) =>
    data.map((elem, index) => {
        const normalizeValue = Math.floor((elem / maxValues[index]) * maxTicksLimit)
        // 그래프를 초과하지 않도록 최대 값을 제한합니다
        return normalizeValue > maxTicksLimit ? maxTicksLimit : normalizeValue
    })

interface RadarProps {
    labelList: string[]
    mainData: number[]
    backgroundData: number[]
    maxValues: number[] // 넘어온 값을 100단위로 정규화 할 때 사용
}
const getRadarChartData = ({labelList, mainData, backgroundData, maxValues}: RadarProps) => {
    return {
        labels: labelList,
        datasets: [
            {
                label: '또래 평균',
                fill: true,
                borderColor: GRAPH_COLOR.background.border,
                backgroundColor: GRAPH_COLOR.frontBackground.background,
                pointRadius: 0,
                borderWidth: 1.2,
                data: getNormalize(backgroundData, maxValues),
                animation: {
                    duration: 0, // 이 데이터셋의 애니메이션 비활성화
                },
            },
            {
                label: DATASET_LABEL,
                fill: true,
                borderColor: GRAPH_COLOR.main.border,
                backgroundColor: GRAPH_COLOR.main.background,
                pointRadius: 0,
                borderWidth: 1.2,
                data: getNormalize(mainData, maxValues),
                animation: {
                    duration: 320,
                    delay: 250,
                },
            },
            {
                label: '또래 평균',
                fill: true,
                borderColor: GRAPH_COLOR.background.border,
                backgroundColor: GRAPH_COLOR.background.background,
                pointRadius: 0,
                borderWidth: 1.2,
                data: getNormalize(backgroundData, maxValues),
                animation: {
                    duration: 0, // 이 데이터셋의 애니메이션 비활성화
                },
            },
        ],
    }
}

export const Radar = ({coverages, debug = false}: {coverages: GraphCoverages; debug?: boolean}) => {
    const [tooltipStatus, setTooltipStatus] = useState<{display: boolean; index: number; left: number; top: number}>(
        styleInitState,
    )

    const canvasRef = useRef<HTMLCanvasElement>(null)
    const chartRef = useRef<Chart | null>()

    const {labelList, mainData, backgroundData, maxValues} = useMemo(
        () =>
            coverages.reduce(
                (acc, curr) => ({
                    labelList: [...acc.labelList, curr.labelName],
                    mainData: [...acc.mainData, curr.myAmount],
                    backgroundData: [...acc.backgroundData, curr.peerAmount],
                    maxValues: [...acc.maxValues, curr.maxAmount],
                }),
                {
                    labelList: [],
                    mainData: [],
                    backgroundData: [],
                    maxValues: [],
                } as {
                    labelList: string[]
                    mainData: number[]
                    backgroundData: number[]
                    maxValues: number[]
                },
            ),
        [coverages],
    )

    const coverage = useMemo(() => {
        const tooltipIndex = tooltipStatus.index
        return tooltipIndex >= 0
            ? coverages[tooltipStatus.index]
            : // 툴팁이 노출되지 않을 땐 index가 -1. 위치 정보를 얻기위해 툴팁의 opacity를 제어해서 노출.
              // 따라서 보이진 않아도 컴포넌트를 그려야하기 때문에 의미없는 목 데이터가 필요함
              {
                  condition: '',
                  myRank: 0,
                  labelName: '',
                  myAmount: 0,
                  peerAmount: 0,
                  maxAmount: 0,
              }
    }, [coverages, tooltipStatus.index])

    const handleClickTooltip = useCallback(() => {
        setTooltipStatus(styleInitState)
    }, [])

    useEffect(() => {
        if (!canvasRef.current) {
            return
        }

        Chart.register(RadarController, RadialLinearScale, LineElement, PointElement, Filler)

        // 새로운 차트 인스턴스를 생성합니다.
        const radarChart = new Chart(canvasRef.current, {
            type: 'radar',
            data: getRadarChartData({labelList, mainData, backgroundData, maxValues}),
            plugins: [labelDrawPlugin],
            options,
        })

        const clickEvent = (event: MouseEvent) => {
            const ctx = radarChart.ctx

            const rect = radarChart.canvas.getBoundingClientRect()
            const {x: mouseX, y: mouseY} = getRelativeCanvasPosition({
                x: event.clientX,
                y: event.clientY,
                canvasWidth: radarChart.width,
            })

            // 마우스 클릭 지점을 검정색 점으로 표시
            debug && drawPoint(mouseX, mouseY, 'black', ctx)

            const labels = radarChart.data.labels as string[]
            const radius = (Math.min(radarChart.width, radarChart.height) / 2) * 0.85 // 차트 반지름

            const {x: canvasX, y: canvasY} = getRelativeCanvasPosition({
                x: rect.left,
                y: rect.top,
                canvasWidth: radarChart.width,
            })

            const centerX = canvasX + radarChart.width / 2 // 차트 중앙 x 좌표
            const centerY = canvasY + radarChart.height / 2 // 차트 중앙 y 좌표

            // 그래프의 중심부를 파란색 점으로 표시
            debug && drawPoint(centerX, centerY, 'blue', ctx)

            // 지금 클릭한 지점이 8개의 라벨 영역 밖인 경우를 체크
            let checkAllOutside = true

            labels.forEach((label, currentIndex) => {
                const angle = getAngle({index: currentIndex, length: labels.length})
                const {x: labelX, y: labelY} = getLabelPosition({centerX, centerY, radius, angle})
                // 라벨 기준점을 빨간색 점 표시
                debug && drawPoint(labelX, labelY, 'red', ctx)

                // label의 너비 **휴리스틱한 값 적용**
                const textWidth = labelFontSize * label.length + labelPaddingX * 2
                // label의 높이 **휴리스틱한 값 적용**
                const textHeight = labelFontSize + labelPaddingY * 2
                // label의 대각선의 반. label의 좌표를 중심으로 원을 그릴 반지름으로 사용 **휴리스틱한 값 적용**
                const labelRadius = Math.sqrt(textWidth ** 2 + textHeight ** 2) / 2

                // 중심 labelX, labelY, 반지름 labelRadius를 가지는 원의 내부 좌표를 클릭했는지 확인
                const isInside = (mouseX - labelX) ** 2 + (mouseY - labelY) ** 2 < labelRadius ** 2

                if (isInside) {
                    // 툴팁이 노출되는 지점을 클릭한 경우 노란색 점 표시
                    debug && drawPoint(mouseX, mouseY, 'yellow', ctx)

                    // 만약 라벨 원 영역을 클릭했다면 checkAllOutside를 false로 세팅
                    checkAllOutside = false

                    setTooltipStatus((prev) => {
                        const display = prev.index !== currentIndex

                        // 같은 라벨을 다시 클릭한 경우 초기화
                        if (!display) {
                            return styleInitState
                        }

                        const {x: parentStandardX, y: parentStandardY} = getCanvasToParentPosition({
                            x: labelX,
                            y: labelY,
                            canvasWidth: radarChart.width,
                        })

                        const nextTop = parentStandardY + getTooltipTopByIndex(currentIndex)
                        const nextLeft = parentStandardX + getTooltipLeftByIndex(currentIndex) - tooltipWidth / 2

                        return {
                            display,
                            index: currentIndex,
                            left: getWindowCalculatedLeft(nextLeft),
                            top: nextTop,
                        }
                    })
                }
            })

            // 클릭한 지점이 8개의 라벨 영역 밖이면 툴팁 초기화
            if (checkAllOutside) {
                setTooltipStatus(styleInitState)
            }
        }

        radarChart.canvas.addEventListener('click', clickEvent)

        chartRef.current = radarChart

        // 컴포넌트 언마운트 시 차트를 파괴합니다.
        return () => {
            if (chartRef.current) {
                chartRef.current.canvas.removeEventListener('click', clickEvent)
                chartRef.current.destroy()
            }
        }
    }, [backgroundData, debug, labelList, mainData, maxValues])

    useResize(() => {
        if (chartRef.current) {
            chartRef.current.resize()
        }
    })

    return (
        <>
            <RadarTooltip
                isShow={tooltipStatus.display}
                coverage={coverage}
                style={{
                    left: tooltipStatus.left,
                    top: tooltipStatus.top,
                }}
                onClick={handleClickTooltip}
            />
            <canvas ref={canvasRef} width="312" height="312" style={{display: 'block'}} />
        </>
    )
}
